import { useEffect } from 'react';
import type { DeskSnapshot } from '../api/generated/types.gen';
import { num, stamp, time } from '../format';

export function SourcesDrawer({ desk, onClose }: { desk: DeskSnapshot; onClose: () => void }) {
  const c = desk.selected;
  const coin = desk.coin ?? 'BTC', index = desk.settlement_index ?? 'BRTI', isWindow = desk.horizon === '15m';
  useEffect(() => { const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose]);
  return <>
    <div className="scrim" onClick={onClose}/>
    <aside className="drawer" role="dialog" aria-modal="true" aria-label="Sources and rules">
      <div className="panel-head"><h2>Sources &amp; rules</h2><button className="tool" onClick={onClose} autoFocus>Close</button></div>
      <div className="body">
        <section><h3>Where Kalshi’s {coin} price comes from</h3>
          <ol className="chain">
            <li><b>Exchange order books</b><span>Buy and sell orders across eligible {coin}/USD venues.</span></li>
            <li><b>CF Benchmarks {index}</b><span>An index combining those books by its published method.</span></li>
            <li><b>Kalshi settlement average</b><span>The simple average of {index} over the final 60 seconds before the target time.</span></li>
          </ol>
          <p>The chart shows <b>Coinbase {coin}/USD</b>. A crossing on this chart does not decide the contract.</p>
          <p className="note">{index} feed not connected, so distance to the settlement reference is unavailable.</p>
          <p><a href="https://www.cfbenchmarks.com/" target="_blank" rel="noreferrer">CF Benchmarks indices</a></p>
        </section>
        {c && <section><h3>{isWindow ? 'This window' : 'Selected contract'}</h3>
          {isWindow ? <p><b>UP</b> if the final-minute {index} average is at least the target, the average before the window opened; <b>DOWN</b> otherwise.</p>
            : <p><b>YES</b> if the final-minute {index} average is above the threshold; <b>NO</b> otherwise.</p>}
          <p>{c.rule}</p>
          <dl className="facts">
            <dt>Ticker</dt><dd>{c.ticker}</dd>
            <dt>Trading closes</dt><dd>{time(c.close_time)}</dd>
            <dt>Reference window</dt><dd>{time(new Date(Date.parse(c.close_time) - 60000).toISOString())}–{time(c.close_time)}</dd>
            <dt>Expected settlement</dt><dd>{c.expected_settlement ? time(c.expected_settlement) : 'not specified'}</dd>
            <dt>Quotes observed</dt><dd>{stamp(c.observed_at)}</dd>
          </dl>
          {!c.supported && <p className="error">This rule is not supported. Previews are off.</p>}
        </section>}
        <section><h3>Feeds</h3><dl className="facts">{desk.feeds.map(f => <span key={f.source} style={{ display: 'contents' }}><dt>{f.source}</dt><dd>{f.status}{f.age_seconds != null ? ` · ${num(f.age_seconds, 1)}s` : ''}</dd></span>)}</dl></section>
        <section><h3>Calculations</h3>
          <p>Market prices are not calibrated probabilities.</p>
          <dl className="facts">
            <dt>30-day audit</dt><dd>{String(desk.history.status ?? 'waiting')} · {String(desk.history.missing ?? 'pending')} missing</dd>
          </dl>
          <code>view {desk.view_id} · revision {desk.revision}</code>
        </section>
      </div>
    </aside>
  </>;
}
